/**
 * Employee Management Page & Dialog Style Definitions (MUI sx objects)
 * Path: src/styles/employeeManagement.styles.js
 * 
 * Reuses identical styles from Payroll Setup (payrollSetupStyles)
 * to avoid code duplication across modules.
 */
import { payrollSetupStyles } from './payrollSetup.styles';

export const employeeManagementStyles = {
    // --- Reused from Payroll Setup ---
    uploadIcon: payrollSetupStyles.uploadIcon,
    addNewButton: payrollSetupStyles.addNewButton,
    tableWrapperStack: payrollSetupStyles.tableWrapperStack,
    tableWrapperBox: payrollSetupStyles.tableWrapperBox,
    paginationFooter: payrollSetupStyles.paginationFooter,
    tableContainer: payrollSetupStyles.tableContainer,
    bodyScroll: payrollSetupStyles.bodyScroll,
    headerFilterCell: payrollSetupStyles.headerFilterCell,
    bodyCellBase: payrollSetupStyles.bodyCellBase,
    headerCellBase: payrollSetupStyles.headerCellBase,
    tableRowHover: payrollSetupStyles.tableRowHover,
    emptyStateCell: payrollSetupStyles.emptyStateCell,

    // --- Import Modal Styles (Shared with Payroll Setup) ---
    importDialog: payrollSetupStyles.importDialog,
    importToolbar: payrollSetupStyles.importToolbar,
    importTitle: payrollSetupStyles.importTitle,
    exportErrorsBtn: payrollSetupStyles.exportErrorsBtn,
    importSubmitBtn: payrollSetupStyles.importSubmitBtn,
    importCloseIcon: payrollSetupStyles.importCloseIcon,
    importDialogContent: payrollSetupStyles.importDialogContent,
    importTableContainer: payrollSetupStyles.importTableContainer,
    importTableRowHeader: payrollSetupStyles.importTableRowHeader,

    // --- Employee Management Specific Styles ---
    errorContainer: {
        backgroundColor: '#fafafa',
        borderRadius: 2,
        border: '1px dashed #e0e0e0',
        m: 1,
        p: 4
    },
    retryButton: {
        mt: 1
    },
    dialogContent: {
        p: 1,
        flex: 1,
        minHeight: 0,
        overflow: "hidden"
    },

    // --- Employee Profile Subcomponent ---
    profileContainer: {
        scrollbarWidth: "thin",
        scrollbarColor: "#bdbdbd transparent",
        "&::-webkit-scrollbar": { width: 4 },
        "&::-webkit-scrollbar-thumb": { backgroundColor: "#bdbdbd", borderRadius: 10 },
        "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#9e9e9e" },
    },
    autocompleteListbox: {
        maxHeight: 200,
        overflowY: "auto",
        "& .MuiAutocomplete-option": {
            fontSize: 11,
            paddingLeft: "8px",
            paddingRight: "8px",
            borderBottom: "1px solid #0e0e0e",
        },
        "& .MuiAutocomplete-option:last-child": { borderBottom: "none" },
        "& .MuiAutocomplete-option[aria-selected='true']": { backgroundColor: "#2f5d8a !important", color: "white" },
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-thumb": { backgroundColor: "#bdbdbd", borderRadius: 10 },
        "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#9e9e9e" },
    },
    getAutocompleteInputSx: (isDisabled) => ({
        "& .MuiFilledInput-root": {
            height: "25px",
            minHeight: "25px",
            backgroundColor: isDisabled ? "#f5f5f5 !important" : "white",
            paddingTop: 0,
            paddingBottom: 0,
            display: "flex",
            alignItems: "center",
            "&:hover": { backgroundColor: isDisabled ? "#f5f5f5 !important" : "white" },
            "&.Mui-focused": { backgroundColor: isDisabled ? "#f5f5f5 !important" : "white" },
            "&.Mui-disabled": { 
                backgroundColor: "#f5f5f5 !important",
                WebkitTextFillColor: "#000 !important",
                color: "#000 !important",
            },
        },
        "& .MuiFilledInput-input": { 
            padding: "0px 10px",
            fontSize: "12px",
            height: "auto",
            lineHeight: "1.2",
            "&.Mui-disabled": { WebkitTextFillColor: "#000 !important", color: "#000 !important" },
        },
    }),
    compactDropdownSx: {
        "& .MuiFilledInput-root": { height: 25 },
        "& .MuiFilledInput-input": { padding: "0 10px", fontSize: "12px" },
    },
    cloneLinkText: {
        ml: 4,
        cursor: "pointer",
        color: "primary.main",
        textDecoration: "underline",
    },

    // --- Employee Details Accordion ---
    getAccordionSummarySx: (isExpanded) => ({
        borderBottom: isExpanded ? "4px solid #B5DCF2" : "2px solid transparent",
        transition: "border-bottom 0.5s ease",
        "&.Mui-expanded": {
            backgroundColor: "transparent",
        },
        "& .MuiAccordionSummary-content": {
            alignItems: "center",
            margin: 0,
            "&.Mui-expanded": { margin: 0 }
        },
    }),

    // --- Address Details Subcomponent ---
    addressDetailsContainer: {
        p: "2rem",
        display: "flex"
    },
    addressDetailsRow: {
        display: "flex",
        gap: "1.5rem"
    },
    fieldsetColumn: {
        display: "flex",
        flexDirection: "column",
        gap: 1
    },

    // --- Notes & Attachments Subcomponent ---
    notesAndAttachmentsStack: {
        p: 1
    },
    pillLabel: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 160,
        height: 28,
        fontFamily: "Poppins",
        bgcolor: "#b9ffeb",
        border: "1px solid #00a58f",
        borderRadius: "8px",
        fontWeight: 500,
        fontSize: "11px",
        color: "black",
        mb: 1,
        whiteSpace: "nowrap",
    },
    notesTextField: {
        mt: 1,
        bgcolor: "#fff",
        "& .MuiInputBase-input": {
            fontSize: "12px !important",
            p: 0
        }
    },

    // --- Payroll Details Subcomponent ---
    payrollDetailsStack: {
        position: "relative",
        minHeight: "200px"
    },
    payrollDetailsTopRow: {
        mb: 1
    },
    payFrequencyLabelBox: {
        minWidth: 90
    },
    payFrequencyLabelText: {
        fontSize: 12,
        fontWeight: 500,
        whiteSpace: "nowrap",
    },
    payrollFrequencyDropdown: {
        width: 140,
        "& .MuiFilledInput-root": {
            height: 25,
            border: "1px solid #ccc",
        },
    },
    addRowBtn: {
        textTransform: "none",
        fontSize: 12,
        px: 3,
        borderRadius: "4px",
    },
    payrollTableContainer: {
        maxHeight: "auto",
        p: 0,
        border: "1px solid #f5f5f5"
    },
    payrollTable: {
        width: "100%",
        tableLayout: "fixed"
    },
    tableInputHeight30: {
        "& .MuiFilledInput-root": { height: 30 }
    },
    dropdownPopperMaxContent: {
        "& .MuiAutocomplete-paper": {
            width: "max-content",
            minWidth: "100%",
        },
        "& .MuiAutocomplete-listbox": {
            width: "max-content",
        },
        "& .MuiAutocomplete-option": {
            fontSize: "11px",
            whiteSpace: "nowrap",
        },
    },
    tableCellBorderRight: {
        borderRight: "1px solid #eee",
        py: 2
    },
    tableCellBorderRight15: {
        borderRight: "1px solid #eee",
        py: 1.5
    },
    tableCellVerticalTop: {
        verticalAlign: "top",
        borderRight: "1px solid #eee"
    },
    checkboxStack: {
        mt: 1
    },
    tableCellAction: {
        verticalAlign: "top",
        py: 1.5
    },
    fullWidthInput: {
        width: "100%"
    },
    itemText: {
        fontSize: 12,
        fontWeight: 500
    }
};

export default employeeManagementStyles;
